import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { natureLabel, oemOdmLabel, subcategoryLabel } from '../../../utils/classification';

const PrintableForm = ({ formData, onBack, onPrintComplete }) => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Action Buttons */}
        <div className="mb-6 flex gap-4 print:hidden">
          <Button variant="outline" onClick={onBack}>
            <Icon name="ArrowLeft" size={18} />
            Back to Form
          </Button>
          <Button onClick={handlePrint} className="bg-blue-600">
            <Icon name="Printer" size={18} />
            Print Form
          </Button>
          <Button onClick={onPrintComplete} className="bg-green-600">
            <Icon name="Upload" size={18} />
            Continue to Upload
          </Button>
        </div>

        {/* Printable Form */}
        <div className="bg-white p-8 shadow-lg">
          <style>
            {`
              @media print {
                @page {
                  size: A4;
                  margin: 10mm;
                }
                body * {
                  visibility: hidden;
                }
                .print-content, .print-content * {
                  visibility: visible;
                }
                .print-content {
                  position: absolute;
                  left: 0;
                  top: 0;
                  width: 100%;
                  font-size: 10px !important;
                }
                .print-content h1 {
                  font-size: 16px !important;
                  margin-bottom: 3px !important;
                }
                .print-content h2 {
                  font-size: 12px !important;
                  margin-bottom: 6px !important;
                }
                .print-content .field-label {
                  font-size: 9px !important;
                  margin-bottom: 1px !important;
                }
                .print-content .field-value {
                  font-size: 10px !important;
                  padding-bottom: 1px !important;
                }
                .print-content .space-y-3 > * + * {
                  margin-top: 8px !important;
                }
                .print-content .min-h-\\[50px\\] {
                  min-height: 40px !important;
                }
                .print-content .min-h-\\[60px\\] {
                  min-height: 50px !important;
                }
              }
            `}
          </style>

          <div className="print-content">
            {/* Header */}
            <div className="text-center border-b-2 border-blue-600 pb-1 mb-3">
              <h1 className="text-2xl font-bold text-gray-900 mb-0.5">
                SUPPLIER REQUEST FORM
              </h1>
              <p className="text-sm text-gray-600">For New Vendor Requirement</p>
            </div>

            {/* Form Fields */}
            <div className="space-y-3 mb-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="field-label block text-xs font-semibold text-gray-700 mb-0.5">
                    User Name:
                  </label>
                  <div className="field-value border-b border-gray-400 pb-0.5 text-sm">
                    {formData.user_name}
                  </div>
                </div>

                <div>
                  <label className="field-label block text-xs font-semibold text-gray-700 mb-0.5">
                    User Site & Plant:
                  </label>
                  <div className="field-value border-b border-gray-400 pb-0.5 text-sm">
                    {formData.user_site_plant}
                  </div>
                </div>
              </div>

              <div>
                <label className="field-label block text-xs font-semibold text-gray-700 mb-0.5">
                  Required Vendor Name:
                </label>
                <div className="field-value border-b border-gray-400 pb-0.5 text-sm font-medium">
                  {formData.required_vendor_name}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="field-label block text-xs font-semibold text-gray-700 mb-0.5">
                    Vendor Contact:
                  </label>
                  <div className="field-value border-b border-gray-400 pb-0.5 text-sm">
                    {formData.vendor_contact}
                  </div>
                </div>

                <div>
                  <label className="field-label block text-xs font-semibold text-gray-700 mb-0.5">
                    Vendor Email:
                  </label>
                  <div className="field-value border-b border-gray-400 pb-0.5 text-sm">
                    {formData.vendor_email}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4" data-testid="print-classification">
                <div>
                  <label className="field-label block text-xs font-semibold text-gray-700 mb-0.5">
                    Nature of Business:
                  </label>
                  <div className="field-value border-b border-gray-400 pb-0.5 text-sm">
                    {natureLabel(formData.nature_of_business)}
                  </div>
                </div>

                <div>
                  <label className="field-label block text-xs font-semibold text-gray-700 mb-0.5">
                    Supplier Type:
                  </label>
                  <div className="field-value border-b border-gray-400 pb-0.5 text-sm">
                    {oemOdmLabel(formData.oem_odm)}
                  </div>
                </div>

                <div>
                  <label className="field-label block text-xs font-semibold text-gray-700 mb-0.5">
                    Supplier Sub-category:
                  </label>
                  <div className="field-value border-b border-gray-400 pb-0.5 text-sm">
                    {subcategoryLabel(formData.supplier_subcategory)}
                  </div>
                </div>
              </div>

              <div>
                <label className="field-label block text-xs font-semibold text-gray-700 mb-0.5">
                  Reason for Creating New Vendor Code:
                </label>
                <div className="border border-gray-300 p-2 min-h-[60px] rounded text-sm">
                  {formData.reason_for_new_vendor}
                </div>
              </div>

              <div>
                <label className="field-label block text-xs font-semibold text-gray-700 mb-0.5">
                  Requested By:
                </label>
                <div className="field-value border-b border-gray-400 pb-0.5 text-sm">
                  {formData.requested_by}
                </div>
              </div>
            </div>

            {/* Approval Section */}
            <div className="border-2 border-gray-800 p-3 mt-3">
              <h2 className="text-sm font-bold text-gray-900 mb-2">
                PLANT HEAD APPROVAL
              </h2>
              
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="field-label block text-xs font-semibold text-gray-700 mb-0.5">
                      Approved By:
                    </label>
                    <div className="border-b border-gray-400 min-h-[25px]">
                      
                    </div>
                  </div>

                  <div>
                    <label className="field-label block text-xs font-semibold text-gray-700 mb-0.5">
                      Date:
                    </label>
                    <div className="border-b border-gray-400 min-h-[25px]">
                      
                    </div>
                  </div>
                </div>

                <div>
                  <label className="field-label block text-xs font-semibold text-gray-700 mb-0.5">
                    Signature:
                  </label>
                  <div className="border-b border-gray-400 min-h-[50px]">
                    
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Instructions */}
            <div className="mt-3 p-2 bg-gray-100 rounded">
              <p className="text-xs text-gray-700">
                <strong>Instructions:</strong> Print this form, obtain Plant Head's signature, then upload the signed document.
              </p>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-6 p-6 bg-blue-50 border border-blue-200 rounded-lg print:hidden">
          <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <Icon name="Info" size={20} />
            Next Steps
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-blue-800">
            <li>Click "Print Form" to print this document</li>
            <li>Get it signed by the Plant Head</li>
            <li>Scan or photograph the signed document</li>
            <li>Click "Continue to Upload" to submit the signed document</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default PrintableForm;

